import React from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  Check,
  X
} from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

interface Notification {
  _id: string;
  title: string;
  message: string;
  category?: string;
  priority?: string;
  createdAt: string;
  actionUrl?: string;
  actionText?: string;
  isRead?: boolean;
}

const NotificationCard: React.FC<{ notification: Notification }> = ({ notification }) => {
  const { theme } = document.documentElement.classList.contains('dark') ? { theme: 'dark' } : { theme: 'light' };
  const { markAsRead, removeNotification } = useNotifications();
  const navigate = useNavigate();

  const getNotificationIcon = (category?: string) => {
    switch (category) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'urgent': return 'border-l-red-500';
      case 'high': return 'border-l-orange-500';
      case 'medium': return 'border-l-yellow-500';
      case 'low': return 'border-l-green-500';
      default: return 'border-l-gray-300';
    }
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleClick = () => {
    if (notification.actionUrl) {
      markAsRead(notification._id);
      navigate(notification.actionUrl);
    }
  };

  return (
    <div
      className={`p-4 border-l-4 cursor-pointer ${getPriorityColor(notification.priority)} ${
        !notification.isRead 
          ? theme === 'dark' ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50' 
          : ''
      } ${theme === 'dark' ? 'hover:bg-gray-700 bg-gray-800 border-gray-700' : 'hover:bg-gray-50 bg-white border-gray-200'}`}
      onClick={handleClick}
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">{getNotificationIcon(notification.category)}</div>
        <div className="flex-1">
          <h4 className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{notification.title}</h4>
          <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{notification.message}</p>
          <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            {getRelativeTime(notification.createdAt)}
          </p>
          {notification.actionText && (
            <p className={`mt-2 text-xs font-medium ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
              {notification.actionText}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end space-y-1 ml-2">
          {!notification.isRead && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                markAsRead(notification._id);
              }}
              className={`p-1 rounded ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
              title="Mark as read"
            >
              <Check className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              removeNotification(notification._id);
            }}
            className={`p-1 rounded ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
            title="Remove"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;
